import React, { useEffect, useState, useMemo, useContext, useRef } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { ConfigurationContext } from "@contexts/Configuration";
import { SearchContext } from "@contexts/Search";
import LoaderWithIcon from "@common/LoaderWithIcon";
import { getTemplateBuilder } from "./templateRegistry";
import DebugMessage from "../CoverageFinder/CommonComponent/DebugMessage";
import responsiveTemplate from "./DecisionTreeVS2/dynamic-css/responsive-style.css";

import { AuthenticationService, ConfigurationService, AppSettingsService, CommonService, StyleService } from "@services";

const PatientFinder = ({ token, indicationid, zipCode }) => {
  const { appliedConfiguration, setAppliedConfiguration } = useContext(ConfigurationContext);
  const {
    setSearchTermDisplay,
    benefitType,
    setBenefitType,
    productId,
    productName,
    indicationName,
    setProductId,
    setProductName,
    setIndicationName,
  } = useContext(SearchContext);
  const [clientProductConfigurations, setClientProductConfigurations] = useState(null);
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [stylePath, setStylePath] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [debugMode, setDebugMode] = useState(false);
  const [liveTypeId, setLiveTypeId] = useState("");

  useEffect(() => {
    let ignore = false;
    let tokenProducts = [];
    if (token) {
      const jwtPayload = CommonService.parseJwt(token);
      tokenProducts = JSON.parse(jwtPayload.products);
      setProductName(tokenProducts[0].Name);
      setProductId(tokenProducts[0].ProductId);
      setSelectedProduct(tokenProducts[0]);
      const jwtWebsiteType = jwtPayload.websiteType ?? 2;
      const cdnUrl = AppSettingsService.getInstance().getCDNUrl();
      const jwtProjectType = jwtPayload.projectType ?? 8;
      setStylePath(`${cdnUrl}css/${jwtPayload.clientId}_${jwtProjectType}_${tokenProducts[0].ProductId}_${jwtWebsiteType}.css`);
    } else {
      console.log("Token is null or empty");
    }

    async function getConfig() {
      const requestBody = {
        ProductId: tokenProducts[0].ProductId,
      };

      const configResponse = await ConfigurationService.getInstance().getPatientConfiguration(requestBody);
      if (!ignore) {
        applyConfiguration(configResponse);
        setIsLoadingPage(false);
      }
    }

    if (token) {
      AuthenticationService.getInstance().setToken(token);
      getConfig();
    }

    return () => {
      ignore = true;
    };
  }, [token]);
  const applyConfiguration = (configResponse) => {
    if (configResponse) {
      setClientProductConfigurations(configResponse);
      let jwtWebsiteType = Number(CommonService.parseJwt(token).websiteType ?? 2);
      let config = jwtWebsiteType
        ? configResponse.configurations.find(
            (x) => x.generalSetting != null && Number(x.generalSetting.websiteType) === jwtWebsiteType
          )
        : configResponse.configurations[0];

      if (indicationid) {
        config = configResponse.configurations.find(
          (x) =>
            Number(x.indicationId) === Number(indicationid) &&
            x.generalSetting != null &&
            Number(x.generalSetting.websiteType) === jwtWebsiteType
        );
      }
      setAppliedConfiguration(config);
      setBenefitType(config.generalSetting.benefitType.toLowerCase().includes("pharmacy") ? "Pharmacy" : "Medical");
      setAllConfigValues(config, configResponse.indications);

      const updatedCSS = responsiveTemplate
        .replace(/__BREAKPOINT__/g, `${config.generalSetting.mobileBreakpoint}px`)
        .replace(/__BREAKPOINT_PLUS_ONE__/g, `${config.generalSetting.mobileBreakpoint + 1}px`);

      StyleService.getInstance().loadDynamicStyles(updatedCSS);

    } else {
      console.error("Config response is null or empty");
    }
  };
  function setAllConfigValues(config, indications) {
    const benefitTypeId = config.generalSetting?.benefitType?.toLowerCase().includes("pharmacy") ? 2 : 1;
    setBenefitTypeConfigValues(config, benefitTypeId);
    const cfSettings = config.settings;
    if (config.indicationStylesheetURL) {
      setStylePath(config.indicationStylesheetURL);
    }

    if (cfSettings?.findIndex((setting) => setting.settingId === 81 && setting.isEnabled === true) > -1) {
      setDebugMode(true);
    }
    const indicationName = indications.find((ind) => ind.id === config.indicationId)?.name || "";
    setIndicationName(indicationName);
  }
  function setBenefitTypeConfigValues(config, benefitTypeId) {
    setLiveTypeId(benefitTypeId == 2 ? 4 : 1);
  }
  // Get the appropriate template builder based on configuration
  const TemplateBuilder = useMemo(() => {
    if (!appliedConfiguration) return null;
    return getTemplateBuilder(appliedConfiguration);
  }, [appliedConfiguration]);

  // Template props to pass to the template builder
  const templateProps = useMemo(
    () => ({
      appliedConfiguration,
      selectedProduct,
      debugMode,
      liveTypeId,
      token,
      indicationid,
      zipCode,
      // Additional template-specific props can be added here
    }),
    [appliedConfiguration, selectedProduct, debugMode, liveTypeId, token, indicationid, zipCode]
  );

  return (
    <>
      <link type="text/css" rel="stylesheet" href={stylePath} />
      <Box className="norstella-pf-coverageFinderBox" sx={{ flexGrow: 1 }} part="pfCoverageFinderBox">
        {isLoadingPage && (
          <div className="norstella-cf-overlay">
            <LoaderWithIcon></LoaderWithIcon>
          </div>
        )}
        {TemplateBuilder && !isLoadingPage && <TemplateBuilder {...templateProps} />}
        {debugMode && (
          <Grid item>
            <DebugMessage />
          </Grid>
        )}
      </Box>
    </>
  );
};

export default PatientFinder;
